/** @format */
import { useTranslation } from 'next-i18next';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useKeyPress } from '@toucan/client/shared/hooks/use-key-press';

import { useSearchParams } from '@toucan/client/features/search/components/model-search-results/hooks/use-search-params';
import { FilterOption } from '@toucan/client/features/search/components/model-search-results/types';
import { CustomSelect } from '@toucan/client/features/search/components/search-filter/components/custom-select';

import { SearchFilterDropdown } from '../search-filter-dropdown';
import { getSelectedFiltersFromQueryString } from './helpers';
import { SearchFilterProps } from './types';

export const DEFAULT_TEST_ID = 'search-filter';

export const SearchFilter = ({
    'data-testid': dataTestId = DEFAULT_TEST_ID,
    initialIsOpen = false,
    filter,
    id,
    onUpdate,
    onOpen,
    ...otherProps
}: SearchFilterProps) => {
    const { t } = useTranslation();
    const buttonRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLElement>(null);
    const [isOpen, setIsOpen] = useState<boolean>(initialIsOpen);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const { selectedFilters } = useSearchParams();

    const selectedFiltersFromQueryString = useMemo(
        () => getSelectedFiltersFromQueryString(filter, selectedFilters),
        [filter, selectedFilters]
    );

    const [selectedItems, setSelectedItems] = useState<FilterOption[]>(
        selectedFiltersFromQueryString
    );

    async function handleToggleOpen() {
        if (!isOpen && onOpen) {
            setIsLoading(true);
            setIsOpen(!isOpen);
            await onOpen(id);
            setIsLoading(false);
        } else {
            setIsOpen(!isOpen);
        }
    }

    function handleCancel() {
        setSelectedItems(selectedFiltersFromQueryString);
        setIsOpen(false);
        buttonRef.current && buttonRef.current.focus();
    }

    function handleApply() {
        onUpdate?.(filter, selectedItems);
        setIsOpen(false);
        buttonRef.current && buttonRef.current.focus();
    }

    function handleClear() {
        setSelectedItems([]);
    }

    useKeyPress(menuRef.current, ['Escape', 'Esc'], (event: KeyboardEvent) => {
        event.preventDefault();
        handleCancel();
    });

    useEffect(() => {
        setSelectedItems(selectedFiltersFromQueryString);
    }, [selectedFiltersFromQueryString]);

    return (
        <CustomSelect
            id={id}
            isOpen={isOpen}
            isActive={!!selectedItems?.length}
            label={filter?.label}
            closeMenuAriaLabel={t('close menu')}
            openMenuAriaLabel={t('open menu')}
            onClickOut={handleCancel}
            onToggleOpen={handleToggleOpen}
            ref={buttonRef}
            data-testid={dataTestId}
            {...otherProps}
        >
            <SearchFilterDropdown
                filter={filter}
                applyLabel={t('Apply')}
                cancelLabel={t('Cancel')}
                clearLabel={t('Clear filter')}
                onCancel={handleCancel}
                onApply={handleApply}
                onClear={handleClear}
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
                ref={menuRef}
                isLoading={isLoading}
            />
        </CustomSelect>
    );
};
